// ===========================================
// utils/api.js - API utility functions
// ===========================================
import config from './config';

const API_BASE_URL = config.getApiUrl();

// Helper function to get auth headers
const getAuthHeaders = () => {
  const token = localStorage.getItem('access_token');
  return {
    'Content-Type': 'application/json',
    ...(token && { 'Authorization': `Bearer ${token}` })
  };
};

// Generic API call function
const apiCall = async (endpoint, options = {}) => {
  const url = `${API_BASE_URL}${endpoint}`;
  const config = {
    headers: getAuthHeaders(),
    ...options
  };

  const response = await fetch(url, config);
  
  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Network error' }));
    throw new Error(error.error || `HTTP ${response.status}`);
  }
  
  return response.json();
};

// API functions
export const api = {
  // Authentication
  login: (authData) => apiCall('/auth/telegram', {
    method: 'POST',
    body: JSON.stringify(authData)
  }),

  getProfile: () => apiCall('/auth/profile'),

  // Books
  getBooks: (params = {}) => {
    const searchParams = new URLSearchParams(params);
    return apiCall(`/books?${searchParams}`);
  },

  uploadBook: (formData) => {
    const url = `${API_BASE_URL}/books/upload`;
    const token = localStorage.getItem('access_token');
    
    return fetch(url, {
      method: 'POST',
      body: formData,
      headers: {
        ...(token && { 'Authorization': `Bearer ${token}` })
      }
    }).then(async (response) => {
      if (!response.ok) {
        const error = await response.json().catch(() => ({ error: 'Network error' }));
        throw new Error(error.error || error.msg || `HTTP ${response.status}`);
      }
      
      const result = await response.json();
      return result;
    });
  },

  downloadBook: (bookId) => apiCall(`/books/${bookId}/download`),
  
  viewBook: (bookId) => apiCall(`/books/${bookId}/view`),

  // Admin functions
  getPendingUsers: () => apiCall('/admin/pending-users'),
  
  approveUser: (userId) => apiCall(`/admin/approve-user/${userId}`, {
    method: 'POST'
  }),
  
  rejectUser: (userId) => apiCall(`/admin/reject-user/${userId}`, {
    method: 'DELETE'
  }),

  getAdminStats: () => apiCall('/admin/stats'),

  // Admin Book Management
  getAdminBooks: (params = {}) => {
    const searchParams = new URLSearchParams(params);
    return apiCall(`/admin/books?${searchParams}`);
  },

  updateBook: (bookId, bookData) => apiCall(`/admin/books/${bookId}`, {
    method: 'PUT',
    body: JSON.stringify(bookData)
  }),

  deleteBook: (bookId) => apiCall(`/admin/books/${bookId}`, {
    method: 'DELETE'
  }),

  getAllUsers: () => apiCall('/admin/users'),

  assignBookToUsers: (bookId, userIds) => apiCall(`/admin/books/${bookId}/assign`, {
    method: 'POST',
    body: JSON.stringify({ user_ids: userIds })
  }),

  getBookAssignments: (bookId) => apiCall(`/admin/books/${bookId}/assignments`),

  removeBookAssignment: (assignmentId) => apiCall(`/admin/assignments/${assignmentId}`, {
    method: 'DELETE'
  }),

  // Health check
  healthCheck: () => apiCall('/health')
};

export default api;
